<template>
  <div class="c1-nts-overlay" @click.self="$emit('close')">
    <div class="c1-nts">

      <!-- Header -->
      <div class="c1-nts-head">
        <div class="c1-nts-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Task
        </div>
        <button class="c1-nts-close" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div class="c1-nts-body" ref="body">

        <!-- Title -->
        <div class="c1-nts-sec">
          <div class="c1-nts-lbl">Title <span style="font-size:9px;color:var(--c1-t3,#A09E98);font-weight:500">(optional)</span></div>
          <input class="c1-nts-input" v-model="form.title" placeholder="Short task title…" type="text" style="width:100%;box-sizing:border-box" />
        </div>

        <!-- Task Type -->
        <div class="c1-nts-sec">
          <div class="c1-nts-lbl">Task Type</div>
          <div class="c1-nts-pills">
            <div
              v-for="t in taskTypes"
              :key="t.id"
              class="c1-nts-pill"
              :class="{ on: form.type_id === t.id }"
              :style="form.type_id === t.id ? { background: typeColor(t.id), color: '#fff', borderColor: typeColor(t.id) } : {}"
              @click="form.type_id = t.id"
            >{{ t.title || t.name }}</div>
          </div>
        </div>

        <!-- Priority -->
        <div class="c1-nts-sec">
          <div class="c1-nts-lbl">Priority</div>
          <div class="c1-nts-pills">
            <div class="c1-nts-pill c1-nts-pri-high" :class="{ on: form.priority_id === 1 }" @click="form.priority_id = 1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg>
              High
            </div>
            <div class="c1-nts-pill c1-nts-pri-med" :class="{ on: form.priority_id === 2 }" @click="form.priority_id = 2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Medium
            </div>
            <div class="c1-nts-pill c1-nts-pri-low" :class="{ on: form.priority_id === 3 }" @click="form.priority_id = 3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              Low
            </div>
          </div>
        </div>

        <!-- Assign To (required) -->
        <div class="c1-nts-sec" :class="{ 'c1-nts-sec-err': errors.assign }">
          <div class="c1-nts-lbl">Assign To <span class="c1-nts-req">*required</span></div>
          <div class="c1-nts-chips" v-if="form.assign_to_users.length">
            <div v-for="u in form.assign_to_users" :key="u.id" class="c1-nts-chip">
              <span class="c1-nts-chip-av">{{ userInitials(u) }}</span>
              {{ userShortName(u) }}
              <span class="c1-nts-chip-rm" @click="removeUser(u)">×</span>
            </div>
          </div>
          <div class="c1-nts-drop-wrap">
            <div class="c1-nts-user-search" :class="{ focused: userFocused }">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              <input
                ref="userInput"
                class="c1-nts-user-input"
                v-model="userSearch"
                placeholder="Search user…"
                autocomplete="off"
                @input="showUserDrop = true"
                @focus="userFocused = true; showUserDrop = true"
                @blur="userFocused = false"
              />
            </div>
            <div class="c1-nts-user-drop c1-nts-abs-drop" v-show="showUserDrop && filteredUsers.length">
              <div
                v-for="u in filteredUsers.slice(0, 8)"
                :key="u.id"
                class="c1-nts-user-row"
                @mousedown.prevent="addUser(u)"
              >
                <span class="c1-nts-chip-av sm">{{ userInitials(u) }}</span>
                <span>{{ u.full_name || u.username }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Content (required) -->
        <div class="c1-nts-sec" :class="{ 'c1-nts-sec-err': errors.content }">
          <div class="c1-nts-lbl">Content <span class="c1-nts-req">*required</span></div>
          <textarea
            ref="contentInput"
            class="c1-nts-textarea"
            v-model="form.content"
            placeholder="Describe the task…"
            rows="4"
            @focus="scrollToRef('contentInput')"
          ></textarea>
        </div>

        <!-- Tags (required) -->
        <div class="c1-nts-sec" :class="{ 'c1-nts-sec-err': errors.tags }">
          <div class="c1-nts-lbl">Tags <span class="c1-nts-req">*at least 1</span></div>
          <div class="c1-nts-chips" v-if="form.selected_tags.length">
            <div v-for="tag in form.selected_tags" :key="tag.id" class="c1-nts-chip c1-nts-chip-tag">
              #{{ tag.cat_type || tag.title || tag.name }}
              <span class="c1-nts-chip-rm" @click="removeTag(tag)">×</span>
            </div>
          </div>
          <div class="c1-nts-drop-wrap">
            <div class="c1-nts-user-search" :class="{ focused: tagFocused }">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
              <input
                ref="tagInput"
                class="c1-nts-user-input"
                v-model="tagSearch"
                placeholder="Search tag…"
                autocomplete="off"
                @input="showTagDrop = true"
                @focus="tagFocused = true; showTagDrop = true"
                @blur="tagFocused = false"
              />
            </div>
            <div class="c1-nts-user-drop c1-nts-abs-drop" v-show="showTagDrop && filteredTags.length">
              <div
                v-for="tag in filteredTags.slice(0, 8)"
                :key="tag.id"
                class="c1-nts-user-row"
                @mousedown.prevent="addTag(tag)"
              >
                <span class="c1-nts-tag-dot">#</span>
                <span>{{ tag.cat_type || tag.title || tag.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Dates row -->
        <div class="c1-nts-sec c1-nts-dates">
          <div class="c1-nts-date-group">
            <div class="c1-nts-lbl">Start Date</div>
            <input class="c1-nts-input" type="date" v-model="form.start_date" @focus="scrollToRef(null)" />
          </div>
          <div class="c1-nts-date-group">
            <div class="c1-nts-lbl">Start Time</div>
            <input class="c1-nts-input" type="time" v-model="form.start_time" @focus="scrollToRef(null)" />
          </div>
          <div class="c1-nts-date-group">
            <div class="c1-nts-lbl">Due Date</div>
            <input class="c1-nts-input" type="date" v-model="form.end_date" @focus="scrollToRef(null)" />
          </div>
          <div class="c1-nts-date-group">
            <div class="c1-nts-lbl">Due Time</div>
            <input class="c1-nts-input" type="time" v-model="form.end_time" @focus="scrollToRef(null)" />
          </div>
        </div>

        <!-- Sub Type -->
        <div class="c1-nts-sec" v-if="subTypes.length">
          <div class="c1-nts-lbl">Sub Type</div>
          <div class="c1-nts-pills">
            <div
              v-for="st in subTypes"
              :key="st.id"
              class="c1-nts-pill"
              :class="{ on: form.sub_type_id === st.id }"
              @click="form.sub_type_id = st.id"
            >{{ st.title || st.name }}</div>
          </div>
        </div>

        <!-- Private toggle -->
        <div class="c1-nts-sec c1-nts-row">
          <div class="c1-nts-lbl">Private Task</div>
          <div class="c1-nts-toggle" :class="{ on: form.is_private }" @click="form.is_private = !form.is_private">
            <div class="c1-nts-tog-dot"></div>
          </div>
        </div>

        <!-- Error -->
        <div class="c1-nts-err" v-if="errorMsg">{{ errorMsg }}</div>

      </div>

      <!-- Footer (visible on desktop, hidden on mobile to avoid keyboard overlap) -->
      <div class="c1-nts-foot">
        <button class="c1-nts-cancel" @click="$emit('close')">Cancel</button>
        <button class="c1-nts-submit" :class="{ loading: submitting }" @click="submit" :disabled="submitting">
          <span v-if="!submitting">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Create Task
          </span>
          <span v-else>Creating…</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import apiClientCreator from "../../../helpers/api/request";

const TYPE_COLORS = ["#fa8100","#7C5CFC","#C75450","#2D9D5E","#1144bc","#D4A017","#E05A3A","#00796b","#827717","#4275ec"];

export default {
  name: "C1NewTaskSheet",
  props: {
    taskDictionaries: { type: Object, default: () => ({}) },
    salesPersonList: { type: Array, default: () => [] },
    todayTasks: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] }
  },
  data() {
    const { startTime, endTime } = this.calcNextSlot();
    return {
      submitting: false,
      errorMsg: "",
      errors: {},
      userSearch: "",
      tagSearch: "",
      showUserDrop: false,
      showTagDrop: false,
      userFocused: false,
      tagFocused: false,
      form: {
        type_id: 1,
        sub_type_id: 1,
        priority_id: 2,
        assign_to_users: [],
        selected_tags: [],
        title: "",
        content: "",
        start_date: moment().format("YYYY-MM-DD"),
        start_time: startTime,
        end_date: moment().format("YYYY-MM-DD"),
        end_time: endTime,
        is_private: false
      }
    };
  },
  computed: {
    taskTypes() {
      return (this.taskDictionaries && this.taskDictionaries.general && this.taskDictionaries.general.task_types) || [];
    },
    subTypes() {
      return (this.taskDictionaries && this.taskDictionaries.general && this.taskDictionaries.general.task_sub_types) || [];
    },
    filteredUsers() {
      const q = (this.userSearch || "").toLowerCase().trim();
      const selected = this.form.assign_to_users.map(u => u.id);
      return (this.salesPersonList || []).filter(u => {
        if (selected.includes(u.id)) return false;
        if (!q) return true;
        return (u.full_name || u.username || "").toLowerCase().includes(q);
      });
    },
    filteredTags() {
      const q = (this.tagSearch || "").toLowerCase().trim();
      const selected = this.form.selected_tags.map(t => t.id);
      return (this.categories || []).filter(t => {
        if (selected.includes(t.id)) return false;
        const name = (t.cat_type || t.title || t.name || "").toLowerCase();
        if (!q) return true;
        return name.includes(q);
      });
    }
  },
  watch: {
    "form.start_time"(val) {
      if (!val) return;
      const snapped = this.snapTo15(moment(this.form.start_date + " " + val));
      const snappedStr = snapped.format("HH:mm");
      if (snappedStr !== val) {
        this.$nextTick(() => { this.form.start_time = snappedStr; });
      }
      this.form.end_time = snapped.add(25, "minutes").format("HH:mm");
    }
  },
  mounted() {
    document.addEventListener("click", this.onOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutsideClick);
  },
  methods: {
    snapTo15(m) {
      const remainder = m.minutes() % 15;
      if (remainder === 0) return m.clone();
      return m.clone().add(15 - remainder, "minutes").seconds(0).milliseconds(0);
    },
    calcNextSlot() {
      const tasks = this.todayTasks || [];
      const today = moment().format("YYYY-MM-DD");
      let latestEnd = null;
      tasks.forEach(t => {
        const endField = t.end_time || t.due_date || t.end_date;
        if (!endField) return;
        const m = moment(endField);
        if (m.format("YYYY-MM-DD") !== today) return;
        if (!latestEnd || m.isAfter(latestEnd)) latestEnd = m;
      });
      const startBase = latestEnd && latestEnd.isAfter(moment()) ? latestEnd : moment();
      const start = this.snapTo15(startBase);
      const end = start.clone().add(25, "minutes");
      return { startTime: start.format("HH:mm"), endTime: end.format("HH:mm") };
    },
    typeColor(typeId) {
      const idx = this.taskTypes.findIndex(t => t.id === typeId);
      return TYPE_COLORS[idx % TYPE_COLORS.length] || "#7C5CFC";
    },
    userInitials(u) {
      return (u.full_name || u.username || "").split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "?";
    },
    userShortName(u) {
      return (u.full_name || u.username || "").split(" ")[0] || "";
    },
    addUser(u) {
      this.form.assign_to_users.push(u);
      this.userSearch = "";
      this.showUserDrop = false;
      this.$set(this.errors, "assign", false);
    },
    removeUser(u) {
      this.form.assign_to_users = this.form.assign_to_users.filter(x => x.id !== u.id);
    },
    addTag(tag) {
      this.form.selected_tags.push(tag);
      this.tagSearch = "";
      this.showTagDrop = false;
      this.$set(this.errors, "tags", false);
    },
    removeTag(tag) {
      this.form.selected_tags = this.form.selected_tags.filter(x => x.id !== tag.id);
    },
    onOutsideClick(e) {
      if (!e.target.closest(".c1-nts-user-search") && !e.target.closest(".c1-nts-user-drop")) {
        this.showUserDrop = false;
        this.showTagDrop = false;
      }
    },
    // Scroll the body so focused input stays above keyboard
    scrollToRef(refName) {
      this.$nextTick(() => {
        const body = this.$refs.body;
        if (!body) return;
        const el = refName ? this.$refs[refName] : null;
        if (el) {
          const elTop = el.getBoundingClientRect().top;
          const bodyTop = body.getBoundingClientRect().top;
          const offset = elTop - bodyTop - 80;
          if (offset > 0) body.scrollTop += offset;
        }
      });
    },
    async submit() {
      this.errorMsg = "";
      this.errors = {};
      let valid = true;
      if (!this.form.assign_to_users.length) {
        this.$set(this.errors, "assign", true);
        valid = false;
      }
      if (!this.form.content.trim()) {
        this.$set(this.errors, "content", true);
        valid = false;
      }
      if (!this.form.selected_tags.length) {
        this.$set(this.errors, "tags", true);
        valid = false;
      }
      if (!valid) {
        this.errorMsg = "Please fill in all required fields (assign, content, tags).";
        return;
      }
      this.submitting = true;
      try {
        const ids = this.form.assign_to_users.map(u => u.id);
        const cat_ids = this.form.selected_tags.map(t => t.id);
        const startMoment = moment(this.form.start_date + " " + this.form.start_time);
        const endMoment = moment(this.form.end_date + " " + this.form.end_time);

        const payload = {
          adding_task_spent_time: null,
          assign_to: ids,
          assign_to_group: [],
          title: this.form.title || undefined,
          content: this.form.content,
          cat_arr: cat_ids,
          connected_task: [],
          end_time: endMoment,
          position: null,
          priority_id: this.form.priority_id,
          subTasks: [],
          start_time: startMoment,
          type_id: this.form.type_id,
          sub_type_id: this.form.sub_type_id,
          time_quantity: Math.max(endMoment.diff(startMoment, "hours"), 1),
          task_read_only: [],
          isPrivate: this.form.is_private,
          repeat_task: null
        };

        const res = await apiClientCreator("tasks/new").post("", payload);
        // API returns error string (not HTTP error) if cat_arr invalid
        if (typeof res.data === "string" && res.data.includes("ERROR")) {
          this.errorMsg = res.data;
          return;
        }

        this.$emit("fetchTasks");
        this.$emit("close");
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.errorMsg = msg || "Failed to create task. Please try again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style>
.c1-nts-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 3000;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}
.c1-nts {
  width: 440px;
  max-width: 100vw;
  /* Use dvh if available so sheet shrinks when keyboard opens */
  height: 100vh;
  height: 100dvh;
  background: var(--c1-bg, #F7F6F3);
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 32px rgba(0,0,0,.18);
  animation: c1-nts-in .22s cubic-bezier(.25,.46,.45,.94);
}
@keyframes c1-nts-in {
  from { transform: translateX(-60px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
}
.c1-nts-head {
  display: flex; align-items: center;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid var(--c1-border, #E5E2DC);
  flex-shrink: 0;
}
.c1-nts-title {
  font-size: 15px; font-weight: 700;
  color: var(--c1-t1, #2C2925);
  display: flex; align-items: center; gap: 8px;
  flex: 1;
}
.c1-nts-title svg { width: 18px; height: 18px; stroke: #fa8100; }
.c1-nts-close {
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: var(--c1-surface, #EDECEA); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--c1-t2, #7A7067); transition: background .15s;
}
.c1-nts-close:hover { background: #E0DDD8; }
.c1-nts-close svg { width: 14px; height: 14px; }

/* Body scrolls — this is the key for mobile keyboard */
.c1-nts-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.c1-nts-sec {
  padding: 12px 16px 10px;
  border-bottom: 1px solid var(--c1-border, #E5E2DC);
  transition: background .15s;
}
.c1-nts-sec-err { background: #fff8f7; }
.c1-nts-lbl {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .6px;
  color: var(--c1-t3, #A09E98);
  margin-bottom: 8px;
  display: flex; align-items: center; gap: 6px;
}
.c1-nts-req { color: #E05A3A; font-size: 9px; font-weight: 600; text-transform: none; letter-spacing: 0; }
.c1-nts-pills { display: flex; flex-wrap: wrap; gap: 5px; }
.c1-nts-pill {
  padding: 5px 11px; border-radius: 20px;
  border: 1.5px solid var(--c1-border, #E5E2DC);
  font-size: 11px; font-weight: 600;
  color: var(--c1-t2, #7A7067);
  cursor: pointer; transition: all .15s;
  display: flex; align-items: center; gap: 4px;
  background: #fff;
}
.c1-nts-pill:hover { border-color: #fa8100; color: #fa8100; }
.c1-nts-pill svg { width: 12px; height: 12px; }
.c1-nts-pri-high.on { background: #E05A3A !important; border-color: #E05A3A !important; color: #fff !important; }
.c1-nts-pri-med.on  { background: #E8891C !important; border-color: #E8891C !important; color: #fff !important; }
.c1-nts-pri-low.on  { background: #2D9D5E !important; border-color: #2D9D5E !important; color: #fff !important; }
.c1-nts-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
.c1-nts-chip {
  display: flex; align-items: center; gap: 5px;
  padding: 3px 8px 3px 4px;
  background: #FFF4E8; border: 1.5px solid #fa8100;
  border-radius: 20px; font-size: 11px; font-weight: 600; color: #fa8100;
}
.c1-nts-chip-tag { background: #F0ECFF; border-color: #7C5CFC; color: #7C5CFC; }
.c1-nts-chip-av {
  width: 20px; height: 20px; border-radius: 50%;
  background: #fa8100; color: #fff;
  font-size: 9px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.c1-nts-chip-av.sm { width: 22px; height: 22px; background: var(--c1-dp, #1a56db); }
.c1-nts-chip-rm { cursor: pointer; margin-left: 2px; opacity: .7; font-size: 14px; line-height: 1; }
.c1-nts-chip-rm:hover { opacity: 1; }
.c1-nts-tag-dot {
  width: 22px; height: 22px; border-radius: 50%;
  background: #F0ECFF; color: #7C5CFC;
  font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.c1-nts-user-search {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1.5px solid var(--c1-border, #E5E2DC);
  border-radius: 8px; padding: 9px 12px;
}
.c1-nts-user-search.focused { border-color: #fa8100; }
.c1-nts-user-search > svg { width: 14px; height: 14px; color: var(--c1-t3); flex-shrink: 0; }
.c1-nts-user-input {
  border: none; outline: none; background: transparent;
  font-size: 14px; color: var(--c1-t1, #2C2925);
  font-family: inherit; width: 100%;
  /* Prevent iOS zoom on focus */
  font-size: 16px;
}
.c1-nts-drop-wrap {
  position: relative;
}
.c1-nts-user-drop {
  background: #fff; border: 1.5px solid var(--c1-border, #E5E2DC);
  border-radius: 8px; margin-top: 4px; overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,.10);
}
.c1-nts-abs-drop {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 200;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
}
.c1-nts-user-row {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 10px; cursor: pointer;
  font-size: 13px; color: var(--c1-t1, #2C2925);
  transition: background .1s;
}
.c1-nts-user-row:hover, .c1-nts-user-row:active { background: var(--c1-surface, #EDECEA); }
.c1-nts-textarea {
  width: 100%; border: 1.5px solid var(--c1-border, #E5E2DC);
  border-radius: 8px; padding: 10px 12px;
  /* 16px prevents iOS auto-zoom */
  font-size: 16px; font-family: inherit;
  color: var(--c1-t1, #2C2925); background: #fff;
  resize: vertical; outline: none; transition: border-color .15s;
  box-sizing: border-box;
}
.c1-nts-textarea:focus { border-color: #fa8100; }
.c1-nts-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.c1-nts-input {
  width: 100%; border: 1.5px solid var(--c1-border, #E5E2DC);
  border-radius: 8px; padding: 9px 10px;
  font-size: 16px; font-family: inherit;
  color: var(--c1-t1, #2C2925); background: #fff;
  outline: none; transition: border-color .15s;
  box-sizing: border-box;
}
.c1-nts-input:focus { border-color: #fa8100; }
.c1-nts-row {
  display: flex; align-items: center;
  justify-content: space-between;
  padding-top: 14px; padding-bottom: 14px;
}
.c1-nts-row .c1-nts-lbl { margin-bottom: 0; }
.c1-nts-toggle {
  width: 38px; height: 22px; border-radius: 11px;
  background: var(--c1-surface, #EDECEA);
  cursor: pointer; position: relative; transition: background .2s;
}
.c1-nts-toggle.on { background: #fa8100; }
.c1-nts-tog-dot {
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff; position: absolute;
  top: 3px; left: 3px; transition: left .2s;
  box-shadow: 0 1px 4px rgba(0,0,0,.2);
}
.c1-nts-toggle.on .c1-nts-tog-dot { left: 19px; }
.c1-nts-err {
  margin: 8px 16px 0;
  padding: 10px 12px;
  background: #FFF2EE; border: 1px solid #E05A3A;
  border-radius: 7px; font-size: 12px; color: #E05A3A; font-weight: 600;
}
/* Inline submit button inside body (shown on mobile) */
.c1-nts-inline-submit {
  border-bottom: none;
  padding-bottom: 20px;
}
/* Footer (hidden on mobile when keyboard is open) */
.c1-nts-foot {
  display: flex; gap: 8px; padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--c1-border, #E5E2DC);
  background: #fff; flex-shrink: 0;
}
/* Hide footer on small screens — submit is in body instead */
@media (max-height: 500px) {
  .c1-nts-foot { display: none; }
}
.c1-nts-cancel {
  flex: 1; padding: 12px; border-radius: 9px;
  border: 1.5px solid var(--c1-border, #E5E2DC);
  background: transparent; font-size: 14px; font-weight: 600;
  color: var(--c1-t2, #7A7067); cursor: pointer;
  font-family: inherit; transition: background .15s;
}
.c1-nts-cancel:hover { background: var(--c1-surface, #EDECEA); }
.c1-nts-submit {
  flex: 2; padding: 12px; border-radius: 9px; border: none;
  background: #fa8100; font-size: 14px; font-weight: 700; color: #fff;
  cursor: pointer; font-family: inherit;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background .15s, opacity .15s;
  width: 100%;
}
.c1-nts-submit:hover, .c1-nts-submit:active { background: #e07300; }
.c1-nts-submit:disabled { opacity: .6; cursor: not-allowed; }
.c1-nts-submit svg { width: 14px; height: 14px; }
</style>
